/**
 * Iter 65b Section 4 — Notification Preferences Panel
 *
 * Renders the Settings \u2192 Notifications UI:
 *   - One row per email event_type (from the backend `event_types` payload).
 *   - Per row: enable/disable Switch + Frequency dropdown (if allow_batch).
 *   - Auto-saves on change (debounced) \u2014 no explicit Save button.
 *   - Accepts a `highlightType` prop; that row gets a gold ring + scrollIntoView.
 *     Used by the email-footer "Manage notification preferences" deep link.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Bell, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function NotificationPreferencesPanel({ workspaceId, highlightType }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Only show the "Saved" affordance AFTER the first real save in a session;
  // otherwise the panel falsely looks like it just persisted defaults on mount.
  const [hasSavedAtLeastOnce, setHasSavedAtLeastOnce] = useState(false);
  const [prefs, setPrefs] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [freqOptions, setFreqOptions] = useState([
    { value: 'realtime',     label: 'Real-time' },
    { value: 'hourly_batch', label: 'Hourly batch' },
    { value: 'off',          label: 'Off' },
  ]);
  const highlightRef = useRef(null);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/notifications/preferences/${workspaceId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setPrefs(body.preferences || {});
      setEventTypes(body.event_types || []);
      if (Array.isArray(body.frequency_options) && body.frequency_options.length) {
        setFreqOptions(body.frequency_options);
      }
    } catch (e) {
      toast.error('Could not load notification preferences');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Scroll + flash the highlighted row once data is loaded.
  useEffect(() => {
    if (!highlightType || loading) return;
    const el = highlightRef.current;
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightType, loading]);

  // Debounced save \u2014 fires 400ms after the last toggle/change so rapid
  // clicks don\u2019t spam the API.
  const saveTimer = useRef(null);
  const queueSave = useCallback((nextPrefs) => {
    setPrefs(nextPrefs);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await fetch(`${API_URL}/notifications/preferences/${workspaceId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
          },
          body: JSON.stringify({ preferences: nextPrefs }),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setHasSavedAtLeastOnce(true);
      } catch (e) {
        toast.error('Preference save failed \u2014 retry?');
      } finally {
        setSaving(false);
      }
    }, 400);
  }, [workspaceId]);

  const toggleEnabled = (evKey, enabled) => {
    const current = prefs[evKey] || { enabled: true, frequency: 'realtime' };
    queueSave({ ...prefs, [evKey]: { ...current, enabled } });
  };
  const setFrequency = (evKey, frequency) => {
    const current = prefs[evKey] || { enabled: true, frequency: 'realtime' };
    // If they pick 'off', also flip enabled=false so the two controls stay
    // coherent. If they pick something else and enabled=false, flip it on.
    queueSave({
      ...prefs,
      [evKey]: {
        enabled: frequency !== 'off' && current.enabled,
        frequency,
      },
    });
  };

  const filteredFreqOptions = useMemo(() => freqOptions.map(o => ({ ...o })), [freqOptions]);

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="notification-preferences-panel">
      <CardHeader className="pb-3 flex items-start justify-between">
        <div>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Bell className="h-4 w-4 text-gold" />
            Email notifications
          </CardTitle>
          <p className="text-[11px] text-muted-foreground/60 mt-1">
            Choose which events email you \u2014 toggle off what\u2019s noisy. In-app notifications stay on regardless.
          </p>
        </div>
        {saving && (
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/60" data-testid="notif-prefs-saving">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving\u2026
          </span>
        )}
        {!saving && !loading && hasSavedAtLeastOnce && (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400/80" data-testid="notif-prefs-saved">
            <CheckCircle2 className="h-3 w-3" /> Saved
          </span>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-10 flex justify-center text-[12px] text-muted-foreground/50">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading preferences\u2026
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 pb-2 mb-2 border-b border-border/20">
              <span className="flex-1 text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Event</span>
              <span className="w-40 text-left text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Frequency</span>
              <span className="w-16 text-center text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-wider">Email</span>
            </div>
            <div className="space-y-1" data-testid="notif-prefs-list">
              {eventTypes.map((meta) => {
                const row = prefs[meta.event_type] || { enabled: meta.default_enabled, frequency: meta.default_frequency };
                const isHighlighted = highlightType === meta.event_type;
                const rowClass = `flex items-center gap-4 py-2.5 rounded-md px-2 transition-colors ${
                  isHighlighted
                    ? 'bg-gold/10 ring-1 ring-gold/40'
                    : 'hover:bg-foreground/[0.02]'
                }`;
                return (
                  <div
                    key={meta.event_type}
                    ref={isHighlighted ? highlightRef : null}
                    className={rowClass}
                    data-testid={`notif-pref-row-${meta.event_type}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[12.5px] font-medium">{meta.label}</p>
                      <p className="text-[10.5px] text-muted-foreground/55 mt-0.5">{meta.description}</p>
                    </div>
                    <div className="w-40">
                      {meta.allow_batch ? (
                        <Select
                          value={row.frequency}
                          onValueChange={(v) => setFrequency(meta.event_type, v)}
                        >
                          <SelectTrigger
                            className="h-8 text-[11.5px] bg-background/40 border-border/30"
                            data-testid={`notif-pref-freq-${meta.event_type}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredFreqOptions.map(o => (
                              <SelectItem key={o.value} value={o.value} className="text-[11.5px]">
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-[11px] text-muted-foreground/50 pl-2">Real-time only</span>
                      )}
                    </div>
                    <div className="w-16 flex justify-center">
                      <Switch
                        checked={row.enabled}
                        onCheckedChange={(v) => toggleEnabled(meta.event_type, v)}
                        data-testid={`notif-pref-toggle-${meta.event_type}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10.5px] text-muted-foreground/45 mt-4 leading-relaxed">
              Transactional emails (password reset, account invitations) always send and can\u2019t be turned off.
              If any single workspace sends you 20+ emails in 24 hours, the 21st+ are bundled into a summary
              digest so your inbox never gets flooded.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
